import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Spinner,
  Textarea,
  Label,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useCancelRequestDialogStyles } from './CancelRequestDialog.styles';

const MAX_REASON_LENGTH = 500;

interface CancelRequestDialogProps {
  open: boolean;
  eventTitle: string;
  onConfirm: (comments: string) => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
  message?: string | null;
}

export const CancelRequestDialog = ({
  open,
  eventTitle,
  onConfirm,
  onCancel,
  loading = false,
  error = null,
  message = null,
}: CancelRequestDialogProps) => {
  const { t } = useTranslation('dashboard');
  const styles = useCancelRequestDialogStyles();
  const [comments, setComments] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setComments('');
    setValidationError(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  const handleConfirm = useCallback(() => {
    const trimmed = comments.trim();
    if (trimmed.length === 0) {
      setValidationError(t('cancelDialog.reasonRequired'));
      return;
    }
    if (trimmed.length > MAX_REASON_LENGTH) {
      setValidationError(t('cancelDialog.reasonTooLong'));
      return;
    }
    setValidationError(null);
    void onConfirm(trimmed);
  }, [comments, onConfirm, t]);

  const handleDismiss = useCallback(() => {
    resetForm();
    onCancel();
  }, [onCancel, resetForm]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleDismiss()}>
      <DialogSurface aria-describedby="cancel-dialog-description">
        <DialogBody>
          <DialogTitle>{t('cancelDialog.title')}</DialogTitle>
          <DialogContent id="cancel-dialog-description">
            <div className={styles.content}>
              {message ? (
                <p className={styles.bodyText} role="alert">
                  {message}
                </p>
              ) : (
                <>
                  <p className={styles.bodyText}>{t('cancelDialog.bodyMessage')}</p>
                  <p className={styles.confirmQuestion}>
                    {t('cancelDialog.confirmQuestion')}
                    <br />
                    <strong>{eventTitle}</strong>
                  </p>
                  <div>
                    <Label htmlFor="cancel-reason" required>
                      {t('cancelDialog.reasonLabel')}
                    </Label>
                    <Textarea
                      id="cancel-reason"
                      className={styles.textarea}
                      value={comments}
                      onChange={(_, data) => {
                        setComments(data.value);
                        if (validationError) setValidationError(null);
                      }}
                      placeholder={t('cancelDialog.reasonPlaceholder')}
                      maxLength={MAX_REASON_LENGTH}
                      disabled={loading}
                    />
                    <span className={styles.charCount}>
                      {comments.length}/{MAX_REASON_LENGTH}
                    </span>
                  </div>
                  {validationError && (
                    <p className={styles.errorText} role="alert">
                      {validationError}
                    </p>
                  )}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            {!message && (
              <BasicButton
                variant="primary"
                size="medium"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? (
                  <span className={styles.loadingButton}>
                    <Spinner size="tiny" />
                    {t('cancelDialog.cancellingButton')}
                  </span>
                ) : (
                  t('cancelDialog.confirmButton')
                )}
              </BasicButton>
            )}
            <BasicButton
              variant="secondary"
              size="medium"
              onClick={handleDismiss}
              disabled={loading}
            >
              {message ? t('cancelDialog.closeButton') : t('cancelDialog.dismissButton')}
            </BasicButton>
          </DialogActions>
          {error && (
            <p className={styles.errorText} role="alert">
              {error}
            </p>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

CancelRequestDialog.displayName = 'CancelRequestDialog';
